import React, { FC, useState } from 'react';

import { FlexibleModal } from '@savgroup-front-common/core/atoms/modal/FlexibleModal/FlexibleModal';
import { MainScreen } from '@savgroup-front-common/core/atoms/VideoCall/VideoCall.types';
import { useCurrentUser } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { retryImportChunk } from '@savgroup-front-common/core/helpers/retryImportChunk';
import { BaseLoader } from '@savgroup-front-common/core/molecules/BaseLoader';

import { useImportAttachmentOnFile } from '../../../../../../hooks/Attachments/useImportAttachmentOnFile';
import { useGetOwnerSummary } from '../../../../../../hooks/Owner/useGetOwnerSummary';
import { useVideoCallLauncherContext } from '../../../VideoCallLauncher/VideoCallLauncher.context';

const VideoCall = React.lazy(() =>
  retryImportChunk(
    () => import('@savgroup-front-common/core/atoms/VideoCall/VideoCall'),
  ),
);

export const FloatingWindowVideoCall: FC = React.memo(() => {
  const {
    applicationId,
    sessionId,
    token,
    shouldDisplayVideoCall,
    onExitCall,
    handleSessionEnd,
    isSessionEndLoading,
    fileId,
    ownerId,
  } = useVideoCallLauncherContext();

  const [isMinimised, setIsMinimised] = useState(false);

  const { firstname: firstName, lastname: lastName } = useCurrentUser();

  const { ownerSummary: ownerInfo } = useGetOwnerSummary({
    ownerId,
  });

  const { handleImportImageOnFile } = useImportAttachmentOnFile({
    fileId,
  });

  const isVideoCallDisplay = !!(
    shouldDisplayVideoCall &&
    applicationId &&
    token &&
    sessionId
  );

  if (!applicationId || !sessionId || !token) {
    return null;
  }

  if (!ownerInfo) {
    return <BaseLoader />;
  }

  return (
    <FlexibleModal
      isOpen={isVideoCallDisplay}
      onRequestClose={() => {
        return undefined;
      }}
      isMinimised={isMinimised}
      initWidth={350}
      shouldKeepRatio
      minWidth={350}
    >
      <VideoCall
        applicationId={applicationId}
        sessionId={sessionId}
        token={token}
        publisherPreference={{
          name: `${firstName} ${lastName}`,
          publishVideo: false,
        }}
        subscriberPreference={{
          name: `${ownerInfo.firstName} ${ownerInfo.lastName}`,
        }}
        main={MainScreen.SUBSCRIBER}
        onClose={onExitCall}
        isMinimised={isMinimised}
        onMinimiseClick={() => setIsMinimised(true)}
        onRestoreClick={() => setIsMinimised(false)}
        onEndCallClick={handleSessionEnd}
        isLoading={isSessionEndLoading}
        onTakeSubscriberPhoto={handleImportImageOnFile}
      />
    </FlexibleModal>
  );
});

FloatingWindowVideoCall.displayName = 'FloatingWindowVideoCall';
